import React from 'react';
import { Mail, BookOpen } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { HelpData } from '../../../types/aai';
import { useAsync } from '../useAsync';
import { Loading, ErrorNote } from '../components';
import { Card, Hero, MonoLabel, PageHeader } from '../dashboards/ui';

const HelpCommunityPage: React.FC = () => {
  const { data, loading, error } = useAsync<HelpData>(() => aaiApi.getHelp(), 'Could not load this.');
  if (loading) return <Loading label="Loading…" />;
  if (error || !data) return <ErrorNote message={error || 'Something went wrong.'} />;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Help & community"
        subtitle="Stuck, curious, or want to share a win? Start here."
      />

      {data.coach ? (
        <Hero className="flex items-center gap-4 mb-4">
          <span className="w-12 h-12 rounded-full bg-accent-500 text-black grid place-items-center font-bold text-lg shrink-0">
            {data.coach.name.charAt(0).toUpperCase()}
          </span>
          <div className="flex-1 min-w-0">
            <MonoLabel tone="accent" className="mb-1">Ask your coach</MonoLabel>
            <div className="text-lg font-bold leading-tight">{data.coach.name}</div>
            <p className="text-[12.5px] text-white/60 mt-0.5">Your AI coach — usually replies within a few hours.</p>
          </div>
          <a
            href={`mailto:${data.coach.email}`}
            className="inline-flex items-center gap-1.5 bg-white text-black text-[12.5px] font-semibold px-4 py-2 rounded-full whitespace-nowrap shrink-0 hover:bg-white/90"
          >
            <Mail size={14} /> Message
          </a>
        </Hero>
      ) : (
        <Card className="mb-4">
          <MonoLabel className="mb-1.5">Your coach</MonoLabel>
          <p className="text-[13px] text-gray-500">A coach will be assigned as your program rolls out.</p>
        </Card>
      )}

      <Card>
        <MonoLabel className="mb-3">Quick resources</MonoLabel>
        {data.resources.length === 0 ? (
          <p className="text-[13px] text-gray-400">Resources are being added by your program team.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.resources.map((r, i) => (
              <div key={i} className="bg-white rounded-lg px-3.5 py-3.5 border border-gray-100">
                <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 grid place-items-center mb-2.5"><BookOpen size={16} /></span>
                <div className="font-semibold text-[13.5px] leading-snug">{r.title}</div>
                <div className="text-[12px] text-gray-500 mt-1 leading-snug">{r.detail}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default HelpCommunityPage;
